import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import QuestionAnalysis from '../components/InterviewResults/QuestionAnalysis';
import { Test } from '../redux/Slices/InterviewSlice/types';
import { useLazyFetchHRTestByIdQuery } from '../services/hrApi';
import { useGetColorCodesQuery } from '../services/startTestAPI/resultsTestApi';

const HRInterviewResults = ({
  propsInterviewResults
}: {
  propsInterviewResults?: Test;
}) => {
  const { id, userId } = useParams();
  const navigate = useNavigate();
  const [fetchHRTestById, { data, isSuccess, isLoading }] =
    useLazyFetchHRTestByIdQuery({});
  const { data: colorCodeData, isSuccess: colorCodeSuccess } =
    useGetColorCodesQuery({}, { refetchOnMountOrArgChange: true });
  const [interviewFullResults, setInterviewFullResults] = useState<Test>(
    {} as Test
  );
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);

  useEffect(() => {
    if (isSuccess && !propsInterviewResults) {
      setInterviewFullResults(data?.data);
    } else if (propsInterviewResults) {
      setInterviewFullResults(propsInterviewResults);
    }
  }, [data, isSuccess]);

  useEffect(() => {
    if (propsInterviewResults) return;
    const userTestId = id;
    fetchHRTestById({ userTestId, userId });
  }, []);

  if (isLoading) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-gradient-to-br from-indigo-100 via-purple-50 to-blue-100'>
        <div className='text-center'>
          <div className='mx-auto mb-4 h-16 w-16 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent'></div>
          <p className='text-gray-600'>Processing results...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-indigo-100 via-purple-50 to-blue-100 px-4 py-8'>
      <div className='mx-auto max-w-6xl'>
        <div className='mb-6'>
          <button
            onClick={() => navigate('/results')}
            className='group flex items-center gap-2 rounded-lg bg-white px-4 py-2 shadow-md transition-all duration-200 hover:bg-gray-50 hover:shadow-lg'
          >
            <svg
              className='h-5 w-5 text-gray-600 transition-transform duration-200 group-hover:-translate-x-1'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M15 19l-7-7 7-7'
              />
            </svg>
            <span className='font-medium text-gray-700'>Back to Results</span>
          </button>
        </div>

        <div className='mb-8 rounded-2xl bg-gradient-to-r from-[#667eea] to-[#764ba2] px-6 py-8 text-center text-white shadow-xl'>
          <h1 className='mb-2 text-3xl font-bold md:text-4xl'>
            {interviewFullResults?.testSet?.testName} Results
          </h1>
        </div>

        <div className='rounded-2xl bg-white p-8 shadow-xl'>
          {interviewFullResults?.answers?.map((eachQuestion, index: number) => (
            <QuestionAnalysis
              key={eachQuestion?.questionId || index}
              questionNo={index + 1}
              question={eachQuestion}
              allQuestions={interviewFullResults?.testSet?.questions}
              isPlayingAudio={isPlayingAudio}
              setIsPlayingAudio={setIsPlayingAudio}
              shouldShowIdealAnswer={false}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default HRInterviewResults;
